import { ReactNode, useId, useState } from "react";
import { FoodsIcon, FoodsTextInput, FoodsTooltip, Typography } from "@foodpilot/foods";
import { Box, Stack, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import type { IngredientEcsBreakdown } from "src/api/client-api/products/useProductLivePreview";
import { formatNumber } from "src/utils/formatting";
import { ImpactScoreColumn } from "./impactScoreColumn";

export const formatImpactPts = (value: number, language: string): string => {
    const formatted = formatNumber(Math.abs(value), language, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    if (value < 0) {
        return `- ${formatted}`;
    }

    return formatted;
};

type ImpactRowProps = {
    label: string;
    value: string | null;
    labelColor?: string;
    valueColor?: string;
    labelVariant?: "medium" | "semibold";
    valueSize?: "micro" | "small";
    trailingSlot?: ReactNode;
    unit: string;
    valueTooltip?: string;
};

export const ImpactRow = (props: ImpactRowProps) => {
    const {
        label,
        value,
        labelColor,
        valueColor,
        labelVariant = "medium",
        valueSize = "micro",
        trailingSlot,
        unit,
        valueTooltip,
    } = props;
    const theme = useTheme();

    const typographyVariant = labelVariant === "semibold" ? "body-semibold" : "small-medium";
    const resolvedLabelColor =
        labelColor ?? (labelVariant === "semibold" ? theme.palette.text.primary : theme.custom.grey[2000]);

    const valueColumn = (
        <ImpactScoreColumn
            value={value}
            valueColor={valueColor}
            valueSize={valueSize}
            trailingSlot={trailingSlot}
            unit={unit}
        />
    );

    return (
        <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
            <Typography
                variant={typographyVariant}
                sx={{ color: resolvedLabelColor, flex: 1, minWidth: 0, paddingRight: theme.spacing(1) }}
            >
                {label}
            </Typography>
            {valueTooltip !== undefined && value === null ?
                <FoodsTooltip title={valueTooltip} variant="simple">
                    <Box component="span" sx={{ display: "inline-flex" }}>
                        {valueColumn}
                    </Box>
                </FoodsTooltip>
            :   valueColumn}
        </Stack>
    );
};

const ECOSYSTEM_DETAIL_KEYS = ["hedges", "plot_size", "crop_diversity", "permanent_pasture"] as const;

type EcosystemServicesDisclosureProps = {
    breakdown: IngredientEcsBreakdown;
    currentLanguage: string;
    green: string;
    scoreUnit: string;
};

export const EcosystemServicesDisclosure = (props: EcosystemServicesDisclosureProps) => {
    const { breakdown, currentLanguage, green, scoreUnit } = props;
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const detailsId = useId();
    const theme = useTheme();
    const detailLabels: Record<(typeof ECOSYSTEM_DETAIL_KEYS)[number], string> = {
        hedges: t("product.v2.recipe.ecosystem.hedges", "Hedges"),
        plot_size: t("product.v2.recipe.ecosystem.plot_size", "Plot size"),
        crop_diversity: t("product.v2.recipe.ecosystem.crop_diversity", "Crop diversity"),
        permanent_pasture: t("product.v2.recipe.ecosystem.permanent_pasture", "Permanent grassland"),
    };

    return (
        <>
            <Box
                component="button"
                type="button"
                aria-controls={detailsId}
                aria-expanded={expanded}
                onClick={() => setExpanded((current) => !current)}
                sx={{
                    appearance: "none",
                    backgroundColor: "transparent",
                    border: 0,
                    color: "inherit",
                    cursor: "pointer",
                    padding: theme.spacing(0),
                    textAlign: "start",
                    width: "100%",
                    "&:focus-visible": {
                        outline: `${theme.spacing(0.25)} solid ${theme.palette.primary.main}`,
                        outlineOffset: theme.spacing(0.25),
                    },
                }}
            >
                <ImpactRow
                    label={t("product.v2.recipe.ecosystem_services", "Ecosystem services")}
                    value={formatImpactPts(breakdown.ecosystemServicesPts, currentLanguage)}
                    unit={scoreUnit}
                    labelColor={green}
                    valueColor={green}
                    trailingSlot={
                        <Box
                            aria-hidden
                            sx={{
                                color: green,
                                display: "inline-flex",
                                transform: expanded ? "rotate(180deg)" : "none",
                                transition: theme.transitions.create("transform", {
                                    duration: theme.transitions.duration.shortest,
                                }),
                            }}
                        >
                            <FoodsIcon icon="arrowDownShort" size={1.5} />
                        </Box>
                    }
                />
            </Box>
            <Box
                id={detailsId}
                hidden={!expanded}
                sx={{ paddingLeft: theme.spacing(1.5), paddingTop: theme.spacing(0.5) }}
            >
                <Stack sx={{ gap: theme.spacing(0.5) }}>
                    {ECOSYSTEM_DETAIL_KEYS.map((key) => (
                        <ImpactRow
                            key={key}
                            label={detailLabels[key]}
                            value={formatImpactPts(breakdown.ecosystemServicesDetail[key], currentLanguage)}
                            labelColor={green}
                            valueColor={green}
                            unit={scoreUnit}
                        />
                    ))}
                </Stack>
            </Box>
        </>
    );
};

type QuantityFieldProps = {
    label: string;
    value: number | null;
    required?: boolean;
    error?: boolean;
    onChange?: (value: string) => void;
};

export const QuantityField = (props: QuantityFieldProps) => {
    const { label, value, required = false, error = false, onChange } = props;
    const displayLabel = required && !label.trimEnd().endsWith("*") ? `${label}*` : label;
    const theme = useTheme();
    const { t } = useTranslation();
    const inputId = useId();
    const errorId = `${inputId}-error`;
    const [draft, setDraft] = useState<string | null>(null);
    const displayValue = draft ?? (value === null ? "" : String(Math.round(value)));

    return (
        <Stack flex={1} minWidth={0} sx={{ gap: theme.spacing(1) }}>
            <Typography
                component="label"
                htmlFor={inputId}
                variant="body-semibold"
                sx={{ color: theme.palette.text.primary }}
            >
                {displayLabel}
            </Typography>
            <FoodsTextInput
                error={error}
                value={displayValue}
                unit="g"
                onChange={(next) => {
                    if (next !== "" && !/^\d*[.,]?\d*$/.test(next)) {
                        return;
                    }
                    setDraft(next);
                    if (next === "") {
                        onChange?.("");
                    } else {
                        const parsed = Number(next.replace(",", "."));
                        if (Number.isFinite(parsed)) onChange?.(String(Math.round(parsed)));
                    }
                }}
                ExtraInputProps={{
                    id: inputId,
                    inputProps: {
                        inputMode: "numeric",
                        "aria-label": displayLabel,
                        "aria-required": required,
                        "aria-invalid": error,
                        "aria-describedby": error ? errorId : undefined,
                    },
                    onBlur: () => setDraft(null),
                    placeholder: "0",
                    type: "text",
                    sx: {
                        "&.MuiInputBase-root": {
                            ...theme.typography["body-regular"],
                            backgroundColor: error ? theme.custom.red[100] : theme.custom.grey[100],
                            borderColor: error ? theme.palette.error.main : theme.custom.strokes.containerDivider.color,
                            borderRadius: theme.spacing(1),
                            borderStyle: theme.custom.strokes.containerDivider.style,
                            borderWidth: theme.custom.strokes.containerDivider.width,
                            paddingInline: theme.spacing(2),
                        },
                    },
                }}
                StackProps={{ sx: { flex: 1, minWidth: 0 } }}
            />
            {error && (
                <Typography id={errorId} variant="small-regular" sx={{ color: theme.palette.error.main }}>
                    {t("form.required_field", "This field is required.")}
                </Typography>
            )}
        </Stack>
    );
};
